/**
 * The record of what is between two civilisations (ADR-0049, ADR-0060), and nothing
 * else: finding it and creating it. Split out so the modules that read relations —
 * memories, attitude, deals — need not import war and peace, nor war and peace them.
 */

import { NONE, type GameState, type Id, type Relation } from '../state/types.js';

export function ordered(x: Id, y: Id): [Id, Id] {
  return x < y ? [x, y] : [y, x];
}

/** The record for a pair, or null when nothing has ever happened between them. */
export function relationOf(state: GameState, x: Id, y: Id): Relation | null {
  const [a, b] = ordered(x, y);
  return state.relations.find((r) => r.a === a && r.b === b) ?? null;
}

/** The record for a pair, created at peace if there was none. Kept sorted. */
export function ensureRelation(state: GameState, x: Id, y: Id): Relation {
  const existing = relationOf(state, x, y);
  if (existing) return existing;
  const [a, b] = ordered(x, y);
  const relation: Relation = {
    a,
    b,
    war: false,
    since: 0,
    lossesA: 0,
    lossesB: 0,
    truceUntil: 0,
    peaceOfferedBy: NONE,
    lastOfferA: -1,
    lastOfferB: -1,
    treatyUntil: 0,
    openBorders: false,
    alliances: [],
    memoriesA: [],
    memoriesB: [],
    lastDealA: -1,
    lastDealB: -1,
    campaignA: null,
    campaignB: null,
    peaceGive: [],
    peaceGet: [],
  };
  state.relations.push(relation);
  state.relations.sort((p, q) => p.a - q.a || p.b - q.b);
  return relation;
}

